import React, { useState } from 'react';
import { 
  Brain, 
  MessageCircle,
  TrendingUp,
  Target,
  Clock,
  Award,
  AlertTriangle,
  CheckCircle,
  Lightbulb,
  ChevronRight,
  Heart,
  Activity,
  Zap,
  Calendar,
  BarChart3,
  Users,
  Smartphone,
  Moon,
  Sun,
  Coffee,
  Utensils,
  Pill,
  Dumbbell
} from 'lucide-react';

interface AIInsight {
  id: string;
  type: 'success' | 'warning' | 'tip' | 'achievement' | 'prediction' | 'correlation';
  title: string;
  message: string;
  confidence: number;
  actionable: boolean;
  category: 'glucose' | 'nutrition' | 'exercise' | 'timing' | 'medication' | 'sleep' | 'stress' | 'mood';
  timestamp: string;
  impact?: 'high' | 'medium' | 'low';
  timeframe?: string;
  relatedFactors?: string[];
}

interface CoachingSession {
  id: string;
  date: string;
  focus: string;
  insights: AIInsight[];
  goals: string[];
  progress: number;
  mood?: number;
  energy?: number;
  stress?: number;
}

interface HealthPrediction {
  id: string;
  metric: string;
  currentValue: number;
  predictedValue: number;
  timeframe: string;
  confidence: number;
  trend: 'improving' | 'stable' | 'declining';
  factors: string[];
}

interface PersonalizedRecommendation {
  id: string;
  type: 'meal' | 'exercise' | 'medication' | 'lifestyle' | 'sleep';
  title: string;
  description: string;
  expectedImpact: string;
  difficulty: 'easy' | 'medium' | 'hard';
  timeRequired: string;
  priority: 'high' | 'medium' | 'low';
  personalizedFor: string[];
}

export const AIHealthCoach: React.FC = () => {
  const [activeSession, setActiveSession] = useState<string>('current');
  const [showChat, setShowChat] = useState(false);
  const [activeTab, setActiveTab] = useState<'insights' | 'predictions' | 'recommendations' | 'mood' | 'progress'>('insights');

  const [currentInsights] = useState<AIInsight[]>([
    {
      id: '1',
      type: 'success',
      title: 'Excellent Glucose Control This Week!',
      message: 'Your average glucose spike this week was 28% lower than last month. The combination of exercise timing and Benefiber supplements is working well.',
      confidence: 92,
      actionable: false,
      category: 'glucose',
      timestamp: '2024-01-15T10:00:00Z',
      impact: 'high'
    },
    {
      id: '2',
      type: 'tip',
      title: 'Optimize Your Evening Meals',
      message: 'Moving your pasta dinner to lunchtime could reduce your glucose spike by an estimated 35 mg/dL based on your day vs night patterns.',
      confidence: 87,
      actionable: true,
      category: 'timing',
      timestamp: '2024-01-15T10:05:00Z',
      impact: 'medium',
      timeframe: 'Next 2 weeks'
    },
    {
      id: '3',
      type: 'warning',
      title: 'Monday Morning Pattern Detected',
      message: 'Your Monday breakfast consistently causes higher spikes. Consider switching to a protein-rich option instead of high-carb foods.',
      confidence: 84,
      actionable: true,
      category: 'nutrition',
      timestamp: '2024-01-15T10:10:00Z',
      impact: 'medium'
    },
    {
      id: '4',
      type: 'achievement',
      title: 'Exercise Consistency Milestone',
      message: 'You\'ve exercised within 24 hours of 85% of your meals this week! This is contributing significantly to your improved glucose control.',
      confidence: 95,
      actionable: false,
      category: 'exercise',
      timestamp: '2024-01-15T10:15:00Z',
      impact: 'high'
    },
    {
      id: '5',
      type: 'prediction',
      title: 'Sleep Quality Impact on Glucose',
      message: 'Based on your sleep patterns, you\'re likely to see 15% higher glucose readings tomorrow. Consider an earlier bedtime tonight.',
      confidence: 78,
      actionable: true,
      category: 'sleep',
      timestamp: '2024-01-15T10:20:00Z',
      impact: 'medium',
      timeframe: 'Tomorrow'
    },
    {
      id: '6',
      type: 'correlation',
      title: 'Stress-Glucose Connection',
      message: 'Your glucose levels are 23% higher on high-stress days. The 10-minute meditation sessions you started last week are showing positive effects.',
      confidence: 89,
      actionable: true,
      category: 'stress',
      timestamp: '2024-01-15T10:25:00Z',
      impact: 'high',
      relatedFactors: ['meditation', 'stress levels', 'glucose spikes']
    }
  ]);

  const [healthPredictions] = useState<HealthPrediction[]>([
    {
      id: '1',
      metric: 'Average Glucose',
      currentValue: 142,
      predictedValue: 135,
      timeframe: 'Next 30 days',
      confidence: 87,
      trend: 'improving',
      factors: ['Exercise consistency', 'Fiber intake', 'Sleep quality']
    },
    {
      id: '2',
      metric: 'HbA1c',
      currentValue: 6.8,
      predictedValue: 6.5,
      timeframe: 'Next 3 months',
      confidence: 82,
      trend: 'improving',
      factors: ['Diet changes', 'Medication adherence', 'Exercise routine']
    },
    {
      id: '3',
      metric: 'Weight',
      currentValue: 185,
      predictedValue: 182,
      timeframe: 'Next 60 days',
      confidence: 75,
      trend: 'improving',
      factors: ['Calorie deficit', 'Exercise frequency', 'Metabolic rate']
    }
  ]);

  const [personalizedRecommendations] = useState<PersonalizedRecommendation[]>([
    {
      id: '1',
      type: 'meal',
      title: 'Protein-Rich Breakfast',
      description: 'Replace Monday morning carbs with Greek yogurt and berries to reduce glucose spikes by 30%.',
      expectedImpact: '30% reduction in morning glucose spikes',
      difficulty: 'easy',
      timeRequired: '5 minutes',
      priority: 'high',
      personalizedFor: ['Monday patterns', 'Glucose sensitivity']
    },
    {
      id: '2',
      type: 'exercise',
      title: 'Post-Dinner Walk',
      description: 'Add a 15-minute walk after dinner to improve glucose control and sleep quality.',
      expectedImpact: '20% improvement in evening glucose control',
      difficulty: 'medium',
      timeRequired: '15 minutes',
      priority: 'medium',
      personalizedFor: ['Evening glucose patterns', 'Sleep quality']
    },
    {
      id: '3',
      type: 'lifestyle',
      title: 'Stress Management',
      description: 'Practice 10-minute meditation sessions 3 times per week to reduce stress-related glucose spikes.',
      expectedImpact: '25% reduction in stress-related glucose spikes',
      difficulty: 'easy',
      timeRequired: '10 minutes',
      priority: 'high',
      personalizedFor: ['Stress patterns', 'Glucose correlation']
    }
  ]);

  const [coachingSessions] = useState<CoachingSession[]>([
    {
      id: 'current',
      date: 'Today',
      focus: 'Optimizing Meal Timing & Exercise Synergy',
      insights: currentInsights,
      goals: [
        'Reduce Monday morning glucose spikes by 30%',
        'Test moving pasta meals to lunchtime',
        'Maintain 80%+ exercise consistency'
      ],
      progress: 78,
      mood: 8,
      energy: 7,
      stress: 4
    },
    {
      id: 'last-week',
      date: 'Jan 8-14',
      focus: 'Fiber Supplement Optimization',
      insights: [],
      goals: [
        'Test Benefiber timing with different meals',
        'Establish baseline glucose responses',
        'Identify high-impact food triggers'
      ],
      progress: 92,
      mood: 7,
      energy: 6,
      stress: 5
    }
  ]);

  const getInsightIcon = (type: string) => {
    switch (type) {
      case 'success': return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'warning': return <AlertTriangle className="h-5 w-5 text-yellow-600" />;
      case 'tip': return <Lightbulb className="h-5 w-5 text-blue-600" />;
      case 'achievement': return <Award className="h-5 w-5 text-purple-600" />;
      case 'prediction': return <TrendingUp className="h-5 w-5 text-indigo-600" />;
      case 'correlation': return <BarChart3 className="h-5 w-5 text-teal-600" />;
      default: return <Brain className="h-5 w-5 text-gray-600" />;
    }
  };

  const getInsightColor = (type: string) => {
    switch (type) {
      case 'success': return 'border-green-200 bg-green-50';
      case 'warning': return 'border-yellow-200 bg-yellow-50';
      case 'tip': return 'border-blue-200 bg-blue-50';
      case 'achievement': return 'border-purple-200 bg-purple-50';
      case 'prediction': return 'border-indigo-200 bg-indigo-50';
      case 'correlation': return 'border-teal-200 bg-teal-50';
      default: return 'border-gray-200 bg-gray-50';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'glucose': return <Activity className="h-4 w-4" />;
      case 'nutrition': return <Utensils className="h-4 w-4" />;
      case 'exercise': return <Dumbbell className="h-4 w-4" />;
      case 'medication': return <Pill className="h-4 w-4" />;
      case 'sleep': return <Moon className="h-4 w-4" />;
      case 'stress': return <Heart className="h-4 w-4" />;
      case 'mood': return <Sun className="h-4 w-4" />;
      default: return <Brain className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
              <Brain className="h-5 w-5 text-white" />
            </div>
            <h2 className="text-3xl font-bold bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent">
              AI Health Coach
            </h2>
          </div>
          <p className="text-slate-600">Powered by advanced AI analysis and your personal health patterns</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowChat(!showChat)}
            className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-all transform hover:scale-105"
          >
            <MessageCircle className="h-4 w-4" />
            <span>Chat with AI Coach</span>
          </button>
        </div>
      </div>

      {/* Progress Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">This Week's Progress</p>
              <p className="text-2xl font-bold text-slate-800">78%</p>
            </div>
            <Target className="h-8 w-8 text-purple-500" />
          </div>
          <div className="mt-2">
            <div className="w-full bg-slate-200 rounded-full h-2">
              <div className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full" style={{ width: '78%' }}></div>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">AI Insights</p>
              <p className="text-2xl font-bold text-slate-800">{currentInsights.length}</p>
            </div>
            <Brain className="h-8 w-8 text-blue-500" />
          </div>
          <p className="text-xs text-slate-500 mt-1">New recommendations today</p>
        </div>

        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Glucose Improvement</p>
              <p className="text-2xl font-bold text-green-600">-28%</p>
            </div>
            <TrendingUp className="h-8 w-8 text-green-500" />
          </div>
          <p className="text-xs text-slate-500 mt-1">Average spike reduction</p>
        </div>

        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Goals Achieved</p>
              <p className="text-2xl font-bold text-purple-600">5/7</p>
            </div>
            <Award className="h-8 w-8 text-yellow-500" />
          </div>
          <p className="text-xs text-slate-500 mt-1">This week's targets</p>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white rounded-lg border border-slate-200">
        <div className="flex border-b border-slate-200">
          {[
            { id: 'insights', label: 'AI Insights', icon: Brain },
            { id: 'predictions', label: 'Predictions', icon: TrendingUp },
            { id: 'recommendations', label: 'Recommendations', icon: Lightbulb },
            { id: 'mood', label: 'Mood & Energy', icon: Heart },
            { id: 'progress', label: 'Progress', icon: Target }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'text-purple-600 border-b-2 border-purple-600 bg-purple-50'
                  : 'text-slate-600 hover:text-slate-800 hover:bg-slate-50'
              }`}
            >
              <div className="flex items-center justify-center space-x-2">
                <tab.icon className="h-4 w-4" />
                <span>{tab.label}</span>
              </div>
            </button>
          ))}
        </div>

        <div className="p-6">
          {activeTab === 'insights' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-800">Latest AI Insights</h3>
                <div className="flex items-center space-x-2 text-sm text-slate-500">
                  <Brain className="h-4 w-4" />
                  <span>Powered by advanced pattern recognition</span>
                </div>
              </div>
              
              <div className="grid gap-4">
                {currentInsights.map((insight) => (
                  <div key={insight.id} className={`border rounded-lg p-4 ${getInsightColor(insight.type)}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3">
                        {getInsightIcon(insight.type)}
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h4 className="font-semibold text-slate-800">{insight.title}</h4>
                            <div className="flex items-center space-x-1">
                              {getCategoryIcon(insight.category)}
                              <span className="text-xs text-slate-500 capitalize">{insight.category}</span>
                            </div>
                            {insight.impact && (
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                insight.impact === 'high' ? 'bg-red-100 text-red-700' :
                                insight.impact === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-green-100 text-green-700'
                              }`}>
                                {insight.impact} impact
                              </span>
                            )}
                          </div>
                          <p className="text-slate-700 mb-2">{insight.message}</p>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4 text-sm text-slate-500">
                              <span>Confidence: {insight.confidence}%</span>
                              {insight.timeframe && <span>Timeframe: {insight.timeframe}</span>}
                            </div>
                            {insight.actionable && (
                              <button className="text-purple-600 hover:text-purple-700 text-sm font-medium">
                                Take Action
                              </button>
                            )}
                          </div>
                          {insight.relatedFactors && (
                            <div className="mt-2 pt-2 border-t border-slate-200">
                              <p className="text-xs text-slate-500 mb-1">Related factors:</p>
                              <div className="flex flex-wrap gap-1">
                                {insight.relatedFactors.map((factor, index) => (
                                  <span key={index} className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded">
                                    {factor}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'predictions' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-800">Health Predictions</h3>
                <div className="flex items-center space-x-2 text-sm text-slate-500">
                  <TrendingUp className="h-4 w-4" />
                  <span>AI-powered forecasting</span>
                </div>
              </div>
              
              <div className="grid gap-4">
                {healthPredictions.map((prediction) => (
                  <div key={prediction.id} className="bg-white border border-slate-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-slate-800">{prediction.metric}</h4>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        prediction.trend === 'improving' ? 'bg-green-100 text-green-700' :
                        prediction.trend === 'stable' ? 'bg-blue-100 text-blue-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {prediction.trend}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mb-3">
                      <div>
                        <p className="text-sm text-slate-600">Current</p>
                        <p className="text-xl font-bold text-slate-800">{prediction.currentValue}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">Predicted</p>
                        <p className="text-xl font-bold text-purple-600">{prediction.predictedValue}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500">Confidence: {prediction.confidence}%</span>
                      <span className="text-slate-500">{prediction.timeframe}</span>
                    </div>
                    <div className="mt-3 pt-3 border-t border-slate-200">
                      <p className="text-xs text-slate-500 mb-2">Key factors:</p>
                      <div className="flex flex-wrap gap-1">
                        {prediction.factors.map((factor, index) => (
                          <span key={index} className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded">
                            {factor}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'recommendations' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-800">Personalized Recommendations</h3>
                <div className="flex items-center space-x-2 text-sm text-slate-500">
                  <Lightbulb className="h-4 w-4" />
                  <span>Tailored to your patterns</span>
                </div>
              </div>
              
              <div className="grid gap-4">
                {personalizedRecommendations.map((rec) => (
                  <div key={rec.id} className="bg-white border border-slate-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h4 className="font-semibold text-slate-800">{rec.title}</h4>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            rec.priority === 'high' ? 'bg-red-100 text-red-700' :
                            rec.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            {rec.priority} priority
                          </span>
                        </div>
                        <p className="text-slate-700 mb-2">{rec.description}</p>
                        <p className="text-sm text-green-600 font-medium mb-2">{rec.expectedImpact}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 text-sm text-slate-500">
                        <span>Difficulty: {rec.difficulty}</span>
                        <span>Time: {rec.timeRequired}</span>
                      </div>
                      <button className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1 rounded text-sm font-medium">
                        Try This
                      </button>
                    </div>
                    <div className="mt-3 pt-3 border-t border-slate-200">
                      <p className="text-xs text-slate-500 mb-2">Personalized for:</p>
                      <div className="flex flex-wrap gap-1">
                        {rec.personalizedFor.map((factor, index) => (
                          <span key={index} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                            {factor}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'mood' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-800">Mood & Energy Tracking</h3>
                <div className="flex items-center space-x-2 text-sm text-slate-500">
                  <Heart className="h-4 w-4" />
                  <span>How you're feeling</span>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { label: 'Mood', value: 8, icon: Sun, color: 'text-yellow-500' },
                  { label: 'Energy', value: 7, icon: Zap, color: 'text-blue-500' },
                  { label: 'Stress', value: 4, icon: Heart, color: 'text-red-500' }
                ].map((metric) => (
                  <div key={metric.label} className="bg-white border border-slate-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <metric.icon className={`h-5 w-5 ${metric.color}`} />
                        <span className="font-medium text-slate-700">{metric.label}</span>
                      </div>
                      <span className="text-2xl font-bold text-slate-800">{metric.value}/10</span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${metric.color.replace('text-', 'bg-')}`}
                        style={{ width: `${metric.value * 10}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'progress' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-800">Goal Progress</h3>
                <div className="flex items-center space-x-2 text-sm text-slate-500">
                  <Target className="h-4 w-4" />
                  <span>Your health journey</span>
                </div>
              </div>
              
              <div className="space-y-4">
                {coachingSessions[0].goals.map((goal, index) => (
                  <div key={index} className="bg-white border border-slate-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-slate-800">{goal}</h4>
                      <span className="text-sm text-slate-500">In Progress</span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2">
                      <div 
                        className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full"
                        style={{ width: `${Math.random() * 40 + 60}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* AI Chat Interface */}
      {showChat && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800">Chat with AI Health Coach</h3>
              <button
                onClick={() => setShowChat(false)}
                className="text-slate-500 hover:text-slate-700"
              >
                ✕
              </button>
            </div>
            <div className="space-y-4">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                    <Brain className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-purple-800">AI Health Coach</p>
                    <p className="text-purple-700">Hello! I'm here to help you optimize your health journey. What would you like to discuss today?</p>
                  </div>
                </div>
              </div>
              <div className="text-center text-slate-500 text-sm">
                <p>Chat interface coming soon...</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}; 
